// ReminderForm.tsx
import React, {useEffect, useState} from 'react';
import {
  Button,
  Platform,
  ScrollView,
  TouchableOpacity,
  View,
} from 'react-native';
import Modal from 'react-native-modal';
import DateTimePicker from '@react-native-community/datetimepicker';

import Icon from '/component/base/Icon';
import LoadingIndicator from '/component/base/LoadingIndicator';
import RadioButton from '/component/base/RadioButton/RadioButton';
import Text from '/component/base/Text';
import WebTimePicker from '/component/base/WebTimePicker';
import candidateReminderTestIDs from '/constant/TestIDs/CandidateReminder';
import {DOES_NOT_REPEAT, END_DATE, START_DATE} from '/enum/reminders.enum';
import {ACTION_TYPES, EVENT_NAMES, USER_TYPES} from '/enum/tealium.enum';
import {getTealiumUtilDefaultInstance} from '/util/tealium';
import testProperties from '/util/testing/testProperties';

import {
  ReminderData,
  repeatArray,
  toLocalDate,
} from '../ReminderUtils/ReminderUtils.util';

import useReminder from './ReminderForm.hook';
import styles from './ReminderForm.style';

interface ReminderFormProps {
  initialData: ReminderData;
  onDataReceived: (data: ReminderData) => void;
  isEdit: boolean;
  onDelete: () => void;
  saveCheckInReminder: (reminder: ReminderData) => void;
  updateCheckInReminder: (reminder: ReminderData) => void;
  deleteReminder: (reminderId: string) => void;
  candidateId: string;
  candidateName: string;
  coachEmail: string;
  lastPaidRank: string;
  highest_achieved_rank: string;
  userId: string;
}

const ReminderForm: React.FC<ReminderFormProps> = ({
  initialData = {
    startDate: '',
    time: '',
    repeat: '',
    selectedStartDateTime: new Date(),
    selectedTime: new Date(),
  },
  onDataReceived,
  isEdit = false,
  onDelete = () => {},
  saveCheckInReminder,
  updateCheckInReminder,
  deleteReminder,
  candidateId,
  candidateName,
  coachEmail,
  lastPaidRank = '',
  highest_achieved_rank = '',
  userId,
}) => {
  const [isDataChanged, setIsDataChanged] = useState(false);

  const {
    startDate,
    endDate,
    time,
    repeat,
    showDatePicker,
    showTimePicker,
    modalVisible,
    errorModalVisible,
    errorMessage,
    datePickerValue,
    timePickerValue,
    activeDateType,
    endDatePickerValue,
    setActiveDateType,
    setRepeat,
    setShowDatePicker,
    setShowTimePicker,
    setModalVisible,
    setErrorModalVisible,
    handleSave,
    toggleDatePicker,
    onDateChange,
    onTimeChange,
    handleDateDone,
    handleTimeDone,
    dateWithoutTime,
    currentDate,
    isToday,
    handleDelete,
    loading,
    setEndDate,
    showError,
  } = useReminder({
    onDataReceived: (data: ReminderData) => {
      onDataReceived(data); // This should match ReminderData type
    },
    initialData,
    isEdit,
    onDelete,
    saveCheckInReminder,
    deleteReminder,
    coachEmail,
    candidateId,
    candidateName,
    updateCheckInReminder,
    lastPaidRank,
    highest_achieved_rank,
    userId,
  });
  // State to track whether the data has changed

  // Effect to set initial values and check for changes
  useEffect(() => {
    const handler = setTimeout(() => {
      if (initialData) {
        const dataChanged =
          startDate !== initialData?.formattedStartDate ||
          time !== initialData?.formattedTime ||
          repeat !== initialData?.frequency ||
          endDate !== initialData?.endDate;
        setIsDataChanged(dataChanged);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [startDate, time, repeat, endDate, initialData]);

  const handleShowDatePicker = (type: 'start' | 'end') => {
    setActiveDateType(type);

    if (!startDate && type === END_DATE) {
      showError(
        'Select Start Date First',
        'Please select a start date before selecting an end date.',
      );
      return;
    } else {
      toggleDatePicker(true);
    }
  };

  const handleShowTimePicker = () => {
    if (!startDate) {
      showError(
        'Date Required',
        'Please select a date before choosing a time to ensure your reminder is set correctly.',
      );
    } else {
      toggleDatePicker(false);
    }
  };

  const openModal = () => {
    setModalVisible(true);
    setShowDatePicker(false);
    setShowTimePicker(false);
  };

  const closeModal = () => {
    setModalVisible(false);
  };

  const closeErrorModal = () => {
    setErrorModalVisible(false);
  };

  const onWebDateChange = (e: any, type: 'start' | 'end') => {
    const value = e.target.value;
    if (value) {
      const [year, month, day] = value.split('-').map(Number);
      const selectedDate = new Date(year, month - 1, day);
      setActiveDateType(type);
      onDateChange({type: 'set'}, selectedDate, type);
    }
  };
  const onWebTimePickerChange = (value: string | null) => {
    if (value) {
      const [hours, minutes] = value.split(':').map(Number);
      const selectedTime = new Date(timePickerValue || dateWithoutTime);
      selectedTime.setHours(hours, minutes, 0, 0);
      onTimeChange({type: 'set'}, selectedTime);
    }
  };

  const sendEvent = getTealiumUtilDefaultInstance().generateTrackEvent({
    eventName: EVENT_NAMES.checkIn,
  });

  const handleActionAndSave = () => {
    const actionType = isEdit ? ACTION_TYPES.edit : ACTION_TYPES.new;
    sendEvent({
      userType: USER_TYPES.candidate,
      userId: candidateId,
      action: actionType,
      lastPaidRank: lastPaidRank,
      highest_achieved_rank: highest_achieved_rank,
      coach_id: String(userId),
    });

    handleSave();
  };

  if (loading) {
    return <LoadingIndicator style={styles.loadingIndicator} />;
  }
  return (
    <View style={styles.container}>
      <ScrollView
        bounces={false}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={styles.scrollContainer}>
        <View style={styles.content}>
          {/* Date View */}
          <View style={styles.fieldContainer}>
            <Text
              variant="body"
              style={styles.fieldHeading}
              {...testProperties(candidateReminderTestIDs.dateInputLabel)}>
              Start Date
            </Text>
            {Platform.OS === 'web' ? (
              <View style={styles.clickableField}>
                <Text
                  variant="small"
                  testID={candidateReminderTestIDs.dateTextField}
                  style={styles.fieldText}>
                  {startDate}
                </Text>
                <input
                  type="date"
                  value={toLocalDate(datePickerValue || dateWithoutTime)}
                  onChange={e => onWebDateChange(e, 'start')}
                  min={toLocalDate(dateWithoutTime)}
                  style={styles.webHiddenInput as any}
                  onClick={(e: any) =>
                    e.target.showPicker && e.target.showPicker()
                  }
                />
              </View>
            ) : (
              <TouchableOpacity
                testID={candidateReminderTestIDs.dateField}
                style={styles.clickableField}
                onPress={() => handleShowDatePicker('start')}>
                <Text
                  variant="small"
                  testID={candidateReminderTestIDs.dateTextField}
                  style={styles.fieldText}>
                  {startDate}
                </Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Date Picker Mobile */}
          {Platform.OS !== 'web' &&
            showDatePicker &&
            activeDateType === START_DATE && (
              <>
                {Platform.OS === 'ios' ? (
                  <View
                    style={styles.buttonContainer}
                    {...testProperties(null, true)}>
                    <Button
                      title="Done"
                      onPress={handleDateDone}
                      {...testProperties(candidateReminderTestIDs.doneButton)}
                    />
                  </View>
                ) : null}
                <DateTimePicker
                  testID={candidateReminderTestIDs.datePicker}
                  value={datePickerValue || dateWithoutTime}
                  mode="date"
                  display="inline"
                  minimumDate={dateWithoutTime}
                  onChange={onDateChange}
                  style={styles.datePicker}
                />
              </>
            )}

          {/* Time View */}
          <View style={styles.fieldContainer}>
            <Text
              variant="body"
              style={styles.fieldHeading}
              {...testProperties(candidateReminderTestIDs.timeInputLabel)}>
              Time
            </Text>
            {Platform.OS === 'web' ? (
              <TouchableOpacity
                activeOpacity={1}
                onPress={handleShowTimePicker}
                style={[styles.clickableField, {paddingHorizontal: 0}]}>
                <View
                  pointerEvents={!startDate ? 'none' : 'auto'}
                  style={{width: '100%', height: '100%'}}>
                  <WebTimePicker
                    value={
                      time ? timePickerValue.toTimeString().slice(0, 5) : null
                    }
                    onChange={onWebTimePickerChange}
                    style={{width: '100%'}}
                    disabled={!startDate}
                  />
                </View>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                testID={candidateReminderTestIDs.timeField}
                style={styles.clickableField}
                onPress={handleShowTimePicker}>
                <Text
                  variant="small"
                  testID={candidateReminderTestIDs.timeTextField}
                  style={styles.fieldText}>
                  {time}
                </Text>
              </TouchableOpacity>
            )}
          </View>

          {/* Time Picker Mobile */}
          {Platform.OS !== 'web' && showTimePicker && (
            <View {...testProperties(null, true)}>
              {Platform.OS === 'ios' ? (
                <View
                  style={styles.buttonContainer}
                  {...testProperties(null, true)}>
                  <Button
                    title="Done"
                    onPress={handleTimeDone}
                    {...testProperties(candidateReminderTestIDs.doneButton)}
                  />
                </View>
              ) : null}
              <DateTimePicker
                testID={candidateReminderTestIDs.timePicker}
                value={timePickerValue || dateWithoutTime}
                mode="time"
                minimumDate={
                  isToday(datePickerValue) ? currentDate() : dateWithoutTime
                }
                display={Platform.OS === 'ios' ? 'spinner' : 'clock'}
                onChange={onTimeChange}
              />
            </View>
          )}

          {/* Repeat View */}
          <View style={styles.fieldContainer}>
            <Text
              variant="body"
              style={styles.fieldHeading}
              {...testProperties(candidateReminderTestIDs.repeatInputLabel)}>
              Repeat
            </Text>
            <TouchableOpacity
              testID={candidateReminderTestIDs.repeatField}
              style={styles.clickableField}
              onPress={openModal}
              {...testProperties(candidateReminderTestIDs.repeatField, true)}>
              <View style={styles.pickerField}>
                <Text
                  variant="small"
                  testID={candidateReminderTestIDs.repeatTextField}
                  style={styles.fieldText}>
                  {repeat}
                </Text>
                <Icon name="chevron-down" />
              </View>
            </TouchableOpacity>
          </View>

          {/* Repeat Picker Modal */}
          <Modal
            testID={candidateReminderTestIDs.repeatPicker}
            animationIn={Platform.OS === 'web' ? 'slideInRight' : 'slideInUp'}
            animationOut={
              Platform.OS === 'web' ? 'slideOutRight' : 'slideOutDown'
            }
            style={styles.modal}
            isVisible={modalVisible}
            useNativeDriver
            hideModalContentWhileAnimating
            transparent={true}
            onBackdropPress={closeModal}>
            <View style={styles.modalView}>
              <TouchableOpacity
                onPress={closeModal}
                {...testProperties(candidateReminderTestIDs.repeatModal, true)}>
                <View style={styles.modalHeader}>
                  <Icon name="close" />
                  <Text
                    variant="heading"
                    style={styles.headerText}
                    {...testProperties(
                      candidateReminderTestIDs.repeatModalHeader,
                    )}>
                    Repeat
                  </Text>
                </View>
                <View style={styles.radioButtonsContainer}>
                  {repeatArray.map(item => (
                    <RadioButton
                      key={item}
                      testID={candidateReminderTestIDs.radioButton}
                      selected={item === repeat}
                      text={item}
                      onPress={() => {
                        setRepeat(item);
                        setModalVisible(false);
                        setEndDate('');
                      }}
                    />
                  ))}
                </View>
              </TouchableOpacity>
            </View>
          </Modal>

          {/* Error Message Modal Web */}
          {Platform.OS === 'web' && (
            <Modal
              animationIn="slideInRight"
              animationOut="slideOutRight"
              style={styles.modal}
              isVisible={errorModalVisible}
              useNativeDriver
              hideModalContentWhileAnimating
              transparent={true}
              onBackdropPress={closeErrorModal}>
              <View style={styles.modalView}>
                <View style={styles.modalHeader}>
                  <TouchableOpacity
                    onPress={closeErrorModal}
                    accessibilityRole="button"
                    accessibilityLabel="Close error modal">
                    <Icon name="close" />
                  </TouchableOpacity>
                  <Text variant="heading" style={styles.headerText}>
                    {errorMessage.title}
                  </Text>
                </View>
                <View style={styles.radioButtonsContainer}>
                  <Text variant="body" style={styles.errorMessageText}>
                    {errorMessage.message}
                  </Text>
                  <TouchableOpacity
                    style={styles.errorCloseButton}
                    onPress={closeErrorModal}
                    accessibilityRole="button"
                    accessibilityLabel="OK">
                    <Text variant="button" style={styles.errorCloseButtonText}>
                      OK
                    </Text>
                  </TouchableOpacity>
                </View>
              </View>
            </Modal>
          )}

          {/* End Date View */}
          {repeat !== DOES_NOT_REPEAT && (
            <>
              <View style={styles.fieldContainer}>
                <Text
                  variant="body"
                  style={styles.fieldHeading}
                  {...testProperties(
                    candidateReminderTestIDs.endDateFieldLabel,
                  )}>
                  End Date
                </Text>
                {Platform.OS === 'web' ? (
                  <View style={styles.clickableField}>
                    <Text
                      variant="small"
                      testID={candidateReminderTestIDs.endDateTextField}
                      style={styles.fieldText}>
                      {endDate}
                    </Text>
                    <input
                      type="date"
                      value={toLocalDate(endDatePickerValue || dateWithoutTime)}
                      onChange={e => onWebDateChange(e, 'end')}
                      min={toLocalDate(dateWithoutTime)}
                      style={styles.webHiddenInput as any}
                      onClick={(e: any) =>
                        e.target.showPicker && e.target.showPicker()
                      }
                    />
                  </View>
                ) : (
                  <TouchableOpacity
                    testID={candidateReminderTestIDs.endDateButton}
                    style={styles.clickableField}
                    onPress={() => handleShowDatePicker(END_DATE)}
                    {...testProperties(
                      candidateReminderTestIDs.endDateButton,
                      true,
                    )}>
                    <Text
                      variant="small"
                      testID={candidateReminderTestIDs.endDateTextField}
                      style={styles.fieldText}>
                      {endDate}
                    </Text>
                  </TouchableOpacity>
                )}
              </View>

              {Platform.OS !== 'web' &&
                showDatePicker &&
                activeDateType === END_DATE && (
                  <>
                    {Platform.OS === 'ios' ? (
                      <View
                        style={styles.buttonContainer}
                        {...testProperties(null, true)}>
                        <Button
                          title="Done"
                          onPress={handleDateDone}
                          {...testProperties(
                            candidateReminderTestIDs.doneButton,
                          )}
                        />
                      </View>
                    ) : null}
                    <DateTimePicker
                      testID={candidateReminderTestIDs.datePicker}
                      value={endDatePickerValue || dateWithoutTime}
                      mode="date"
                      display="inline"
                      minimumDate={dateWithoutTime}
                      onChange={onDateChange}
                      style={styles.datePicker}
                    />
                  </>
                )}
            </>
          )}
        </View>
      </ScrollView>
      {isEdit ? (
        <View>
          <TouchableOpacity
            onPress={handleDelete}
            testID={candidateReminderTestIDs.deleteReminderButton}
            style={styles.buttonDeleteContainer}
            {...testProperties(
              candidateReminderTestIDs.deleteReminderButton,
              true,
            )}>
            <View style={styles.button}>
              <Icon name={'trash'} style={styles.plusIcon} />
              <Text
                variant="button"
                isBold={true}
                style={styles.buttonText}
                {...testProperties(
                  candidateReminderTestIDs.deleteReminderButtonLabel,
                )}>
                Delete reminder
              </Text>
            </View>
          </TouchableOpacity>
        </View>
      ) : null}
      <TouchableOpacity
        testID={candidateReminderTestIDs.saveButton}
        style={[
          styles.saveButton,
          !isDataChanged ||
          !startDate ||
          !time ||
          !repeat ||
          (Platform.OS !== 'web' && showDatePicker) ||
          (repeat !== DOES_NOT_REPEAT && !endDate)
            ? styles.disableStyle
            : null,
        ]}
        onPress={handleActionAndSave}
        disabled={
          !isDataChanged ||
          !startDate ||
          !time ||
          !repeat ||
          loading ||
          (Platform.OS !== 'web' && showDatePicker)
        }>
        <Text
          variant="button"
          style={styles.saveButtonText}
          {...testProperties(candidateReminderTestIDs.saveButtonLabel)}>
          Save
        </Text>
      </TouchableOpacity>
    </View>
  );
};

export default ReminderForm;
